import App from "../../../vendor_core/core/src/App";
import PointerEvents from "../../../vendor_core/core/src/const/PointerEvents";
import InstanceFactory from "../../../vendor_core/core/src/factories/InstanceFactory";
import { Tween } from "../../../vendor_core/core/src/Tween";
import UIController from "../../../vendor_core/core/src/ui/UIController";
import ConsoleUtil from "../../../vendor_core/core/src/utils/ConsoleUtil";
import GameLoader from "../../../vendor_core/core/src/utils/GameLoader";
import { DefaultLayers } from "../../../vendor_core/core/src/utils/Layers";
import SoundController from "../../../vendor_core/core/src/utils/SoundController";
import ViewManager from "../../../vendor_core/core/src/utils/ViewManager";
import ReelGroup from "../../../vendor_core/core/src/views/ReelGroup";
import VendorPreloadState from "../../../vendor_core/src/states/VendorPreloadState";
import GameSoundVars from "../enums/GameSoundVars";
import BURReelSymbol from "../view/BURReelSymbol";
import ControllerManager from "../../../vendor_core/core/src/utils/ControllerManager";
import ClockController from "../../../vendor_core/core/src/controllers/ClockController";
import { Loader } from "pixi.js";
import BURReelsModel from "../models/BURReelsModel";
import BURCascadeReelGroup from "../view/BURCascadeReelGroup";
import BUREnergyMetersView from "../view/BUREnergyMetersView";
import BURPreloadView from "../view/BURPreloadView";
import { warmUpHistoryImages } from "../view/history/BURHistorySymbolImages";
import { UIComponents } from "../../../vendor_core/core/src/ui/UIComponents";
import { MaybeUndefined } from "../types/BURGameTypes";

// Where the phases after the assets end on the loading bar, as a fraction of the whole loading
const AUDIO_START = BURPreloadView.ASSETS_SHARE;
const AUDIO_DECODE = 0.85;
const SETTINGS_START = 0.9;

export default class BURPreloaderState extends VendorPreloadState {
    protected setupReels() {
        const reelGroup: ReelGroup = InstanceFactory.getClass(
            "ReelGroup",
            true
        );
        ViewManager.getLayer(DefaultLayers.Reels)
            .getCurrentScene()
            .addSubView(reelGroup, "ReelGroup", "reels");

        let symbolData: MaybeUndefined<number[][]> = undefined;
        if (BURReelsModel.last_final_view.length > 0) {
            symbolData = BURReelsModel.last_final_view;
        }

        reelGroup.create(
            ViewManager.getLayer(DefaultLayers.Reels).getCurrentScene(),
            symbolData,
            {
                winLineLayer: "Paylines",
                celеbrationLayer: "PaylineCelebration",
                winSymbolsLayer: "ReelsWin"
            }
        );

        reelGroup.reels.forEach((reel) => {
            reel.symbols.forEach((symbol) => {
                (symbol as BURReelSymbol).playSpineAnim();
            });
        });

        BURReelsModel.setReels(reelGroup as BURCascadeReelGroup);
        BURReelsModel.reels.prewarmSpinePool();

        // Init energy meters
        const energyMeters = new BUREnergyMetersView();
        BURReelsModel.setEnergyMeters(energyMeters);
        energyMeters.init();
        energyMeters.onBetChanged();
    }

    public preloadComplete() {
        App.assets.onResourcesLoaded();
        const preloadLayer = ViewManager.getLayer(DefaultLayers.Preloader);
        this.buildViews();

        ControllerManager.addController(
            "clockController",
            new ClockController()
        );
        const onStartTap = () => {
            this.toggleInitialSounds();
            this.playIdleMusic();
            SoundController.howler.noAudio = true; //TODO: remove this when sounds are implemented
            preloadLayer.clear();
            UIController.addTurboListener();
            this.startGame();
        };

        // The history images are prepared before the start screen is shown, otherwise tapping it right away
        // makes the game freeze.
        warmUpHistoryImages().then(
            () => {
                const scene = preloadLayer.setScene("start");
                scene.interactive = true;
                scene.on(PointerEvents.DOWN, onStartTap);
            }
        );

        App.dispatcher.on("BET_CHANGED", ({ bet }) => {
            BURReelsModel.energy_meters.onBetChanged(bet);
        });

        // Hide the free spins alternative text under the reels
        // because there is a free spins announcer
        ViewManager.getLayer(DefaultLayers.BetBar)
            .getCurrentScene()
            .groups.get(UIComponents.FreeSpinsInfoGroup)!.alpha = 0;
    }

    private showLoadingProgress(fraction: number) {
        (this.preloadView as BURPreloadView).showProgress(fraction);
    }

    /**
     * Loads the music and the sounds together while the loading screen is still shown, so the game
     * can't be started (and the start screen is not shown) before all the audio is ready.
     * Loading it after the start would block the main thread while the player is already in the game.
     */
    protected loadMusic() {
        const audio = [
            ...(GameLoader.config.music ?? []),
            ...(GameLoader.config.sounds ?? [])
        ];
        // The files take AUDIO_START..AUDIO_DECODE, decoding them the rest until SETTINGS_START
        const onProgress = (loader: Loader) =>
            this.showLoadingProgress(
                AUDIO_START +
                    (loader.progress / 100) * (AUDIO_DECODE - AUDIO_START)
            );
        const binding = Loader.shared.onProgress.add(onProgress);
        GameLoader.loadSoundAssets(audio, async () => {
            Loader.shared.onProgress.detach(binding);
            this.showLoadingProgress(AUDIO_DECODE);
            await SoundController.loadSounds(audio);
            this.showLoadingProgress(SETTINGS_START);
            ConsoleUtil.log("Sound resources loaded");
            this.preloadComplete();
        });
    }

    protected playIdleMusic() {
        Tween.delayedCall(1, () => {
            SoundController.setSpecificMusicVolume(GameSoundVars.IDLE_MUSIC, 0);
            SoundController.playMusic(GameSoundVars.IDLE_MUSIC);
        });
    }
}
